/**
 * Leadership Component
 * Single-founder showcase — full-width luxury layout with decorative graphics
 */

import { LEADERSHIP } from '../utils/constants';

const andrew = LEADERSHIP[0];

export function createLeadership(): string {
  return `
    <!-- ════════════════════════════════════
         LEADERSHIP
    ════════════════════════════════════ -->
    <section id="leadership" style="background:#000000; position:relative; overflow:hidden;">

      <!-- ── Background texture ── -->
      <div class="grain" style="position:absolute;inset:0;z-index:0;pointer-events:none;"></div>

      <!-- ── Faint radial glow ── -->
      <div style="position:absolute; inset:0; z-index:0; pointer-events:none;
        background:
          radial-gradient(ellipse 70% 60% at 20% 50%, rgba(201,162,39,0.07) 0%, transparent 65%),
          radial-gradient(ellipse 50% 50% at 85% 20%, rgba(201,162,39,0.05) 0%, transparent 60%);
      "></div>

      <!-- ── Decorative large "A" watermark ── -->
      <div aria-hidden="true" style="
        position:absolute;
        right:-40px;
        bottom:-60px;
        font-family:'Cormorant Garamond',serif;
        font-size:clamp(280px,30vw,480px);
        font-weight:600;
        line-height:1;
        color:rgba(201,162,39,0.04);
        pointer-events:none;
        user-select:none;
        z-index:0;
        letter-spacing:-0.04em;
      ">A</div>

      <div style="max-width:1320px; margin:0 auto; padding:0 48px; position:relative; z-index:2;">

        <!-- ── Section header ── -->
        <div style="padding-top:80px; margin-bottom:60px;">
          <div class="reveal" style="display:flex; align-items:center; gap:18px; margin-bottom:28px;">
            <span class="eyebrow" style="color:rgba(201,162,39,0.8);">Leadership</span>
            <span style="display:block; width:48px; height:1px;
              background:linear-gradient(to right, #C9A227, transparent);"></span>
          </div>
          <h2 class="reveal d1" style="
            font-family:'Cormorant Garamond',serif;
            font-size:clamp(44px,5vw,68px);
            font-weight:500;
            line-height:1.15;
            color:#F8F7F4;
            max-width:700px;">
            Institutional Experience
          </h2>
        </div>

        <!-- ── Main two-col layout ── -->
        <div style="
          display:grid;
          grid-template-columns:1fr 1fr;
          gap:60px;
          align-items:center;
          padding-bottom:80px;
        " class="leadership-grid">

          <!-- ── LEFT: Portrait panel ── -->
          <div class="reveal d1" style="position:relative;">

            <!-- Portrait card -->
            <div style="
              position:relative;
              border-radius:2px;
              overflow:hidden;
              background:#111009;
              border:1px solid rgba(201,162,39,0.15);
              aspect-ratio:4/5;
              display:flex;
              align-items:center;
              justify-content:center;
            ">

              <!-- SVG geometric art — luxury portrait placeholder -->
              <svg viewBox="0 0 480 600" xmlns="http://www.w3.org/2000/svg"
                style="position:absolute;inset:0;width:100%;height:100%;" aria-hidden="true">

                <!-- Deep bg gradient -->
                <defs>
                  <radialGradient id="bgGlow" cx="50%" cy="45%" r="55%">
                    <stop offset="0%"   stop-color="#1a1508" stop-opacity="1"/>
                    <stop offset="100%" stop-color="#0A0908" stop-opacity="1"/>
                  </radialGradient>
                  <radialGradient id="goldGlow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%"   stop-color="#C9A227" stop-opacity="0.18"/>
                    <stop offset="100%" stop-color="#C9A227" stop-opacity="0"/>
                  </radialGradient>
                  <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%"   stop-color="#C9A227" stop-opacity="0"/>
                    <stop offset="40%"  stop-color="#C9A227" stop-opacity="0.6"/>
                    <stop offset="100%" stop-color="#C9A227" stop-opacity="0"/>
                  </linearGradient>
                  <linearGradient id="vertGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%"   stop-color="#C9A227" stop-opacity="0"/>
                    <stop offset="50%"  stop-color="#C9A227" stop-opacity="0.5"/>
                    <stop offset="100%" stop-color="#C9A227" stop-opacity="0"/>
                  </linearGradient>
                  <filter id="softBlur">
                    <feGaussianBlur stdDeviation="8"/>
                  </filter>
                </defs>

                <rect width="480" height="600" fill="url(#bgGlow)"/>

                <!-- Central glow -->
                <ellipse cx="240" cy="260" rx="200" ry="220"
                  fill="url(#goldGlow)" filter="url(#softBlur)"/>

                <!-- Outer circle ring -->
                <circle cx="240" cy="260" r="170"
                  stroke="#C9A227" stroke-width="0.6" fill="none" opacity="0.2"/>
                <circle cx="240" cy="260" r="148"
                  stroke="#C9A227" stroke-width="0.4" fill="none" opacity="0.12"/>

                <!-- Corner bracket — TL -->
                <path d="M40 100 L40 40 L100 40"
                  stroke="#C9A227" stroke-width="1" fill="none" opacity="0.35"/>
                <!-- Corner bracket — TR -->
                <path d="M440 100 L440 40 L380 40"
                  stroke="#C9A227" stroke-width="1" fill="none" opacity="0.35"/>
                <!-- Corner bracket — BL -->
                <path d="M40 500 L40 560 L100 560"
                  stroke="#C9A227" stroke-width="1" fill="none" opacity="0.22"/>
                <!-- Corner bracket — BR -->
                <path d="M440 500 L440 560 L380 560"
                  stroke="#C9A227" stroke-width="1" fill="none" opacity="0.22"/>

                <!-- Top diamond -->
                <polygon points="240,60 256,82 240,104 224,82"
                  stroke="#C9A227" stroke-width="0.9" fill="none" opacity="0.4"/>
                <!-- Small inner diamond -->
                <polygon points="240,72 249,82 240,92 231,82"
                  stroke="#C9A227" stroke-width="0.6" fill="rgba(201,162,39,0.06)" opacity="0.5"/>

                <!-- Horizontal cross-hairs -->
                <line x1="60"  y1="260" x2="180" y2="260"
                  stroke="url(#lineGrad)" stroke-width="0.6" opacity="0.5"/>
                <line x1="300" y1="260" x2="420" y2="260"
                  stroke="url(#lineGrad)" stroke-width="0.6" opacity="0.5"/>

                <!-- Vertical -->
                <line x1="240" y1="60"  x2="240" y2="180"
                  stroke="url(#vertGrad)" stroke-width="0.6" opacity="0.5"/>
                <line x1="240" y1="340" x2="240" y2="540"
                  stroke="url(#vertGrad)" stroke-width="0.6" opacity="0.4"/>

                <!-- Diagonal accent lines -->
                <line x1="70"  y1="80"  x2="200" y2="180"
                  stroke="#C9A227" stroke-width="0.4" opacity="0.1"/>
                <line x1="410" y1="80"  x2="280" y2="180"
                  stroke="#C9A227" stroke-width="0.4" opacity="0.1"/>

                <!-- Monogram "A" -->
                <text x="240" y="310"
                  font-family="Cormorant Garamond, serif"
                  font-size="180"
                  font-weight="500"
                  text-anchor="middle"
                  dominant-baseline="middle"
                  fill="#C9A227"
                  opacity="0.18"
                  letter-spacing="-4">A</text>

                <!-- Floating dot grid — top right quadrant -->
                ${[
      [340, 100], [360, 115], [380, 100], [340, 130], [360, 145],
      [380, 130], [340, 160], [360, 175], [380, 160],
    ].map(([x, y]) =>
      `<circle cx="${x}" cy="${y}" r="1.2" fill="#C9A227" opacity="0.2"/>`
    ).join('')}

                <!-- Floating dot grid — bottom left quadrant -->
                ${[
      [100, 420], [120, 435], [140, 420], [100, 450], [120, 465],
      [140, 450], [100, 480], [120, 495], [140, 480],
    ].map(([x, y]) =>
      `<circle cx="${x}" cy="${y}" r="1.2" fill="#C9A227" opacity="0.14"/>`
    ).join('')}

                <!-- Bottom label bar -->
                <rect x="0" y="530" width="480" height="70"
                  fill="rgba(10,9,8,0.7)"/>
                <line x1="0" y1="530" x2="480" y2="530"
                  stroke="#C9A227" stroke-width="0.8" opacity="0.3"/>

                <text x="240" y="558"
                  font-family="Cinzel, serif"
                  font-size="13"
                  font-weight="600"
                  text-anchor="middle"
                  fill="#C9A227"
                  letter-spacing="4"
                  opacity="0.9">ANDREW Y</text>

                <text x="240" y="580"
                  font-family="Inter, sans-serif"
                  font-size="9"
                  font-weight="500"
                  text-anchor="middle"
                  fill="#F8F7F4"
                  letter-spacing="3"
                  opacity="0.4">FOUNDER &amp; OWNER</text>

              </svg>

              <!-- Top gold bar -->
              <div style="position:absolute;top:0;left:0;right:0;height:2px;
                background:linear-gradient(90deg, transparent 0%, #C9A227 40%, #C9A227 60%, transparent 100%);
                opacity:0.7; z-index:2;"></div>

            </div>


          </div>

          <!-- ── RIGHT: Content ── -->
          <div style="display:flex; flex-direction:column; gap:36px;">

            <!-- Name + role -->
            <div class="reveal d2">
              <div style="
                display:inline-flex;
                align-items:center;
                gap:10px;
                background:rgba(201,162,39,0.1);
                border:1px solid rgba(201,162,39,0.2);
                border-radius:4px;
                padding:6px 16px;
                margin-bottom:20px;
              ">
                <span style="display:block;width:6px;height:6px;border-radius:50%;background:#C9A227;"></span>
                <span style="
                  font-family:'Inter',sans-serif;
                  font-size:11px;
                  font-weight:600;
                  letter-spacing:0.2em;
                  text-transform:uppercase;
                  color:#C9A227;
                ">${andrew.role}</span>
              </div>

              <h3 style="
                font-family:'Cormorant Garamond',serif;
                font-size:clamp(52px,5.5vw,78px);
                font-weight:500;
                color:#F8F7F4;
                line-height:1.0;
                letter-spacing:-0.01em;
                margin-bottom:0;
              ">${andrew.name}</h3>
            </div>

            <!-- Gold rule -->
            <div class="reveal d2" style="
              width:60px; height:1px;
              background:linear-gradient(to right, #C9A227, rgba(201,162,39,0.2));
            "></div>

            <!-- Bio -->
            <p class="reveal d3" style="
              font-family:'Inter',sans-serif;
              font-size:16px;
              line-height:1.8;
              color:rgba(248,247,244,0.5);
              max-width:480px;
            ">${andrew.bio}</p>

            <!-- Credential pills -->
            <div class="reveal d3" style="display:flex; flex-wrap:wrap; gap:10px;">
              ${[
      'Wealth Management',
      'Legacy Planning',
      'Operational Transformation',
      'Strategic Allocation',
      'North America',
      'Asia-Pacific',
    ].map(tag => `
                <span style="
                  font-family:'Inter',sans-serif;
                  font-size:11px;
                  font-weight:500;
                  letter-spacing:0.1em;
                  text-transform:uppercase;
                  color:rgba(248,247,244,0.45);
                  border:1px solid rgba(248,247,244,0.1);
                  border-radius:3px;
                  padding:6px 14px;
                ">${tag}</span>
              `).join('')}
            </div>


          </div>
        </div>
      </div>
    </section>
  `;
}

export default createLeadership;
